<template>
  <div>
    <div v-if="!loading">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Job
          </h1>
        </template>
        <template #actions>
          <page-header-action @click="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            tag="form-button"
            :indicator="downloadingPreviousLayout"
            @clicked="downloadPrevious"
            v-if="previousJob"
          >
            Download {{ previousJob.type.label }}
          </page-header-action>
        </template>
      </page-header>

      <div class="card mb-4" v-if="item">
        <div class="card-body text-center">
          <div class="mb-2">
            <run-item-details :item="item"></run-item-details>

            <template v-if="item.job?.description">
              <strong>Description</strong>
              <br />
              <p class="mb-0">{{ item.job.description }}</p>
            </template>

            <template v-if="!completed">
              <template v-if="!statement">
                <form-button
                  level="primary"
                  :indicator="completing"
                  @clicked="complete()"
                >
                  Complete Job
                </form-button>
              </template>

              <template v-if="item.completed_at">
                <br />
                <p>
                  Job completed by {{ item.completedBy.full_name }} at
                  {{ item.completed_at | date }}
                </p>

                <form-button
                  level="primary"
                  :indicator="downloading"
                  @clicked="downloadStatement()"
                >
                  Download PDF
                </form-button>
              </template>
            </template>

            <template v-if="completed">
              <br />
              <p>
                Thank you for completing the job, please click the link below to
                go back to your dashboard
              </p>
              <button
                type="button"
                class="btn btn-primary"
                @click="goToDashboard()"
              >
                Back To Dashboard
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="text-center" v-if="!!envelope && !envelope.completed_at">
        <h3>Documents</h3>

        <div class="card">
          <div class="card-body text-center">
            <p>The customer has not signed their docusign documents.</p>
            <p>
              Either click the "Sign Documents" button below to capture the
              documents now or click the "Documents Signed In Person" button to
              sign a physical copy of the documents.
            </p>
          </div>
          <table class="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Document Type</th>
                <th>Document Description</th>
                <th>Uploaded</th>
                <th>Signed</th>
                <th>Signed At</th>
                <th></th>
              </tr>
            </thead>
            <tbody v-if="envelope?.envelopeDocuments?.length">
              <tr
                v-bind:key="index"
                v-for="(envelopeDocument, index) in envelope.envelopeDocuments"
              >
                <td>{{ getDocumentTypeName(envelopeDocument) }}</td>
                <td>{{ envelopeDocument.documentable?.description }}</td>
                <td>
                  {{
                    envelopeDocument.documentable?.created_at
                      | date(dateTimeFormat)
                  }}
                </td>
                <td>
                  <envelope-status-badge :status="envelope.status" />
                </td>
                <td>{{ envelope.completed_at }}</td>
                <td>
                  <form-button
                    level="primary"
                    :indicator="downloading === envelopeDocument.identifier"
                    @clicked="downloadDocument(envelopeDocument)"
                  >
                    {{ getDownloadName(envelope) }}
                  </form-button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td class="text-center" colspan="5">
                  No documents found
                </td>
              </tr>
            </tbody>
          </table>

          <div class="card-footer text-center" v-if="envelope">
            <form-button
              level="primary"
              :indicator="embeddedLoading === envelope.identifier"
              @clicked="embeddedSigning(envelope)"
            >
              Sign Documents
            </form-button>

            <form-button
              level="primary"
              :indicator="skippingDocusign === envelope.identifier"
              @clicked="skipDocusign(envelope)"
            >
              Documents Signed In Person
            </form-button>
          </div>
        </div>
      </div>

      <div class="bail-integration" v-if="statement">
        <statement-renderer
          id="statement-renderer"
          :editable="editable"
          :statement="statement"
          :base-url="aisApiUrl"
          navigation="steps"
          @completed="complete()"
          v-if="!completed"
        ></statement-renderer>
      </div>

      <job-notes
        :job="item.job"
        :show="showNotes"
        @saved="addNote"
        @closed="closeNotes"
        v-if="item"
      ></job-notes>
    </div>

    <div v-else>
      <preloader></preloader>
    </div>
    <!-- Docusign embedded signing container -->
    <div
      :style="{ height: containerHeight }"
      ref="embeddedSigningContainer"
    ></div>

    <div class="footer text-left" v-if="showCancelButton">
      <button type="button" class="btn btn-warning " v-on:click="cancel">
        Cancel
      </button>
    </div>

    <!-- Target element for scrolling -->
    <div ref="scrollTarget" style="position: absolute; bottom: 0;"></div>
  </div>
</template>
<script>
import RunItemDetails from '@/components/Modules/Shared/RunItemDetails';
import JobNotes from '@/components/Modules/Job/Children/JobNotes';
import EnvelopeStatusBadge from '@/components/Modules/Shared/EnvelopeStatusBadge';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    RunItemDetails,
    JobNotes,
    EnvelopeStatusBadge,
    PageHeader,
    PageHeaderAction
  },

  props: {
    runIdentifier: {
      type: String,
      required: true
    },
    itemIdentifier: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: true,
      item: null,
      statement: null,
      editable: true,
      downloading: false,
      downloadingPreviousLayout: false,
      completing: false,
      skippingDocusign: false,
      completed: false,
      showNotes: false,
      envelope: undefined,
      job: null,
      dateTimeFormat: 'DD/MM/YYYY HH:mm',
      containerHeight: 'auto',
      embeddedLoading: false,
      showCancelButton: false
    };
  },

  mounted() {
    this.mount();

    let docuSignScript = document.createElement('script');
    docuSignScript.setAttribute('src', 'https://js-d.docusign.com/bundle.js');
    document.head.appendChild(docuSignScript);
  },

  computed: {
    aisApiUrl() {
      return window.AxisUK.ais.api;
    },

    previousJob() {
      if (this.item?.previousJobs?.length) {
        return this.item.previousJobs[this.item.previousJobs.length - 1];
      }

      return null;
    },

    uploadUrl() {
      return `${window.AxisUK.api}/runs/${this.runIdentifier}/items/${this.itemIdentifier}/uploads`;
    }
  },

  methods: {
    mount() {
      window.Ajax.get(
        `runs/${this.runIdentifier}/items/${this.itemIdentifier}?parts=previousJobs`
      ).then(({ data }) => {
        this.item = data;
        this.job = this.item?.job;
        this.envelope = this.job?.envelopes[0];
      });

      window.Ajax.get(
        `runs/${this.runIdentifier}/items/${this.itemIdentifier}/statement`
      )
        .then(({ data }) => {
          this.statement = JSON.stringify(data);

          if (data.completed_at) {
            this.editable = false;
          }

          this.loading = false;
          this.showCancelButton = false;
        })
        .catch(err => {
          this.loading = false;
        });
    },

    downloadStatement() {
      this.downloading = true;

      window.Ajax.download(
        `runs/${this.runIdentifier}/items/${this.itemIdentifier}/download`
      )
        .then(() => (this.downloading = false))
        .catch(() => {
          this.downloading = false;

          window.toastify.error(
            'Oops',
            'An error occurred while downloading the PDF'
          );
        });
    },

    complete() {
      this.completing = true;

      window.Ajax.post(
        `runs/${this.runIdentifier}/items/${this.itemIdentifier}/complete`,
        {}
      )
        .then(({ data }) => {
          this.completing = false;
          this.completed = true;
        })
        .catch(err => {
          this.completing = false;

          window.toastify.error(
            'Oops',
            'An error occurred while completing the job'
          );
        });
    },

    addNote(note) {
      this.item.job.notes.push(note);
    },

    toggleNotes() {
      this.showNotes = !this.showNotes;
    },

    closeNotes() {
      this.showNotes = false;
    },

    goToDashboard() {
      this.route('/');
    },

    downloadPrevious() {
      if (this.previousJob) {
        this.downloadingPreviousLayout = true;

        window.Ajax.download(
          `runs/${this.previousJob.item.run.identifier}/items/${this.previousJob.item.identifier}/download`
        )
          .then(() => (this.downloadingPreviousLayout = false))
          .catch(() => {
            this.downloadingPreviousLayout = false;

            window.toastify.error(
              'Oops',
              'An error occurred while downloading the PDF'
            );
          });
      }
    },

    downloadDocument(document) {
      this.downloading = document.identifier;

      if (this.envelope) {
        window.Ajax.download(
          `/jobs/${this.job.identifier}/envelopes/${this.envelope.identifier}/envelope-documents/${document.identifier}/download`
        )
          .then(() => (this.downloading = undefined))
          .catch(err => {
            this.downloading = undefined;

            window.toastify.error(
              'Oops',
              'An error occurred while downloading the document'
            );
          });
      }
    },

    downloadCombinedDocument(envelope) {
      if (envelope) {
        this.downloading = envelope.identifier;

        window.Ajax.download(
          `/jobs/${this.job.identifier}/envelopes/${envelope?.identifier}/download`
        )
          .then(() => (this.downloading = undefined))
          .catch(err => {
            this.downloading = undefined;

            window.toastify.error(
              'Oops',
              'An error occurred while downloading the documents'
            );
          });
      }
    },

    skipDocusign() {
      if (this.job && this.envelope) {
        this.skippingDocusign = this.envelope.identifier;

        window.Ajax.put(
          `/jobs/${this.job.identifier}/documents-signed-manually`,
          {}
        )
          .then(({ data }) => {
            this.envelope = undefined;
            this.skippingDocusign = undefined;

            this.mount();
          })
          .catch(err => {
            this.skippingDocusign = undefined;

            window.toastify.error(
              'Oops',
              'An error occurred while updating the envelope'
            );
          });
      }
    },

    getDocumentTypeName(envelopeDocument) {
      return (
        envelopeDocument.documentable?.type?.label ||
        envelopeDocument.documentable?.file_name
      );
    },

    getDownloadName(envelope) {
      switch (envelope.status) {
        case 'sent':
          return 'Download Unsigned Document';
        case 'delivered':
          return 'Download Delivered Document';
        case 'completed':
          return 'Download Signed Document';
        case 'declined':
          return 'Download Declined Document';
        default:
          return 'Download Document';
      }
    },

    embeddedSigning(envelope) {
      this.embeddedLoading = envelope.identifier;

      window.Ajax.get(
        `/jobs/${this.job.identifier}/envelopes/${envelope.identifier}/embedded-url`
      )
        .then(({ data }) => {
          if (data.url) {
            this.startEmbeddedSigning(data.url);
          }
        })
        .catch(err => {
          this.embeddedLoading = false;
          window.toastify.error(
            'Oops',
            'An error occurred while loading the envelope'
          );
        });
    },

    startEmbeddedSigning(url) {
      this.containerHeight = '800px';

      window.DocuSign.loadDocuSign(window.AxisUK.docusign.clientIntegrationId)
        .then(docusign => {
          const signing = docusign.signing({
            url: url,
            displayFormat: 'focused',
            style: {
              branding: {
                primaryButton: {
                  backgroundColor: '#bf5329',
                  color: '#fff'
                }
              },
              signingNavigationButton: {
                finishText: 'Complete',
                position: 'bottom-right'
              }
            }
          });

          signing.on('ready', event => {
            this.embeddedLoading = false;
            this.showCancelButton = true;
            this.scrollToBottom();
          });

          signing.on('sessionEnd', event => {
            this.updateEnvelopeStatus(event.sessionEndType);
            this.showCancelButton = false;
            this.containerHeight = 'auto';
          });

          signing.mount(this.$refs.embeddedSigningContainer);
        })
        .catch(ex => {
          console.error('DocuSign initialization failed:', ex);
        });
    },

    updateEnvelopeStatus(status) {
      window.Ajax.put(
        `/jobs/${this.job.identifier}/envelopes/${this.envelope.identifier}/status`,
        { status }
      )
        .then(({ data }) => {
          this.loading = true;
          this.mount();
        })
        .catch(err => {
          this.loading = false;

          window.toastify.error(
            'Oops',
            'An error occurred while completing the job'
          );
        });
    },

    cancel() {
      this.$router.go(0);
    },

    scrollToBottom() {
      this.$refs.scrollTarget?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }
};
</script>
